import { useState, useMemo, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEye,
  faSearch,
  faUsers,
  faChevronLeft,
  faChevronRight,
  faUserTie,
  faPhone,
  faEnvelope,
  faSpinner,
  faFilter,
  faTimes,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { Card } from "../../../components/ui/card";
import AddOwner from "./AddOwner";
import ViewOwner from "./ViewOwner";
import { useAuth } from "../../../hooks";
import { useShopOwners } from "../../../hooks/useShopOwners";
import { toastSuccess, toastError } from "../../../utils/toast";

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage,
}) => {
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 pt-4 border-t border-gray-200">
      <div className="text-sm text-gray-600">
        Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)} to{" "}
        {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{" "}
        owners
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="h-9 px-3 border-gray-300 hover:border-blue-800"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="h-3 w-3" />
        </Button>
        <div className="flex items-center gap-1">
          {pageNumbers.map((number) => (
            <Button
              key={number}
              variant={currentPage === number ? "default" : "outline"}
              size="sm"
              onClick={() => onPageChange(number)}
              className={`h-9 w-9 p-0 ${
                currentPage === number
                  ? "bg-blue-800 hover:bg-blue-900 text-white"
                  : "border-gray-300 hover:border-blue-800 hover:bg-blue-50"
              }`}
            >
              {number}
            </Button>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="h-9 px-3 border-gray-300 hover:border-blue-800"
        >
          <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
};

const OwnerTab = ({ shop }) => {
  const shopId = shop?.shopId;
  const { hasPermission } = useAuth();

  const canManageOwners =
    (hasPermission(["manage_shop_modules"]) ||
      hasPermission(["manage_business_owner"])) &&
    hasPermission(["manage_user_account"]);

  const { owners: hookOwners, loading, refresh } = useShopOwners(shopId);

  const owners = useMemo(() => {
    if (!hookOwners) return [];

    return hookOwners.map((item) => ({
      id: item.shop_owner_id || item.id,
      shop_owner_id: item.shop_owner_id,
      shopId: item.shopId,
      username: item.username || item.user?.username || "N/A",
      owner_name:
        item.user?.full_name || item.full_name || item.owner_name || "N/A",
      full_name: item.user?.full_name || item.full_name || "N/A",
      phone: item.user?.phone || item.phone || "N/A",
      email: item.user?.email || item.email || "N/A",
      added_date: item.added_date
        ? new Date(item.added_date).toISOString().split("T")[0]
        : "N/A",
      status: item.status === "active" ? "Active" : "Inactive",
      created_at: item.created_at || item.user?.created_at,
      updated_at: item.updated_at || item.user?.updated_at,
      user: item.user,
      shop: item.shop,
    }));
  }, [hookOwners]);

  const [searchTerm, setSearchTerm] = useState("");
  const [localSearchTerm, setLocalSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState(null);

  // Advanced Filter Modal State
  const [isAdvFilterOpen, setIsAdvFilterOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState({
    startDate: "",
    endDate: "",
  });

  // Debounce search to prevent UI reload on every keystroke
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchTerm(localSearchTerm);
      setCurrentPage(1);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [localSearchTerm]);

  const filteredOwners = useMemo(() => {
    let result = owners || [];

    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase().trim();
      result = result.filter(
        (o) =>
          o.owner_name?.toLowerCase().includes(search) ||
          o.full_name?.toLowerCase().includes(search) ||
          o.phone?.toLowerCase().includes(search) ||
          o.username?.toLowerCase().includes(search) ||
          o.email?.toLowerCase().includes(search),
      );
    }

    if (isFilterApplied && startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      result = result.filter((owner) => {
        if (!owner.added_date || owner.added_date === "N/A") return false;
        const addedDate = new Date(owner.added_date);
        return addedDate >= start && addedDate <= end;
      });
    }

    return result;
  }, [owners, searchTerm, startDate, endDate, isFilterApplied]);

  const totalItems = filteredOwners.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentOwners = filteredOwners.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, startDate, endDate]);

  const handleAddOwnerSuccess = async () => {
    setIsAddModalOpen(false);
    try {
      await refresh();
      toastSuccess("Owner added successfully!");
    } catch {
      toastError("Owner added but list could not refresh. Please reload.");
    }
  };

  const handleViewOwnerClose = () => {
    setIsViewModalOpen(false);
    setSelectedOwner(null);
  };

  // --- Advanced Filter Handlers ---
  const openAdvFilter = () => {
    setTempFilters({
      startDate: startDate || "",
      endDate: endDate || "",
    });
    setIsAdvFilterOpen(true);
  };

  const applyAdvFilters = () => {
    setStartDate(tempFilters.startDate);
    setEndDate(tempFilters.endDate);
    setIsFilterApplied(!!(tempFilters.startDate || tempFilters.endDate));
    setCurrentPage(1);
    setIsAdvFilterOpen(false);
  };

  const clearAdvFilters = () => {
    setTempFilters({
      startDate: "",
      endDate: "",
    });
    setStartDate("");
    setEndDate("");
    setIsFilterApplied(false);
    setCurrentPage(1);
    setIsAdvFilterOpen(false);
  };

  return (
    <div className="space-y-4">
      {isAddModalOpen && (
        <AddOwner
          onClose={() => setIsAddModalOpen(false)}
          onAdd={handleAddOwnerSuccess}
          onUserAdded={handleAddOwnerSuccess}
          shopId={shopId}
        />
      )}

      {isViewModalOpen && selectedOwner && (
        <ViewOwner owner={selectedOwner} onClose={handleViewOwnerClose} />
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Shop Owners</h3>
          <p className="text-sm text-gray-600">
            Manage owners associated with this shop
          </p>
        </div>
        {canManageOwners && (
          <Button
            onClick={() => setIsAddModalOpen(true)}
            className="gap-2 bg-blue-800 hover:bg-blue-900 text-white shadow-sm"
          >
            <FontAwesomeIcon icon={faPlus} className="h-3.5 w-3.5" />
            Add Owner
          </Button>
        )}
      </div>

      {/* Main Toolbar: Search Left, Advanced Filter Right */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 h-3.5 w-3.5"
          />
          <Input
            type="text"
            placeholder="Search by name, phone, email, or username..."
            value={localSearchTerm}
            onChange={(e) => setLocalSearchTerm(e.target.value)}
            className="pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-800/30 focus:border-blue-800 w-full bg-white text-sm h-10"
          />
        </div>
        <Button
          onClick={openAdvFilter}
          className="gap-2 bg-blue-800 hover:bg-blue-900 text-white w-full sm:w-auto px-6 h-10"
        >
          <FontAwesomeIcon icon={faFilter} />
          Advanced Filter
          {isFilterApplied && (
            <span className="ml-1 w-2 h-2 rounded-full bg-orange-400"></span>
          )}
        </Button>
      </div>

      <Card className="bg-white border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing{" "}
              <span className="font-semibold text-gray-700">{totalItems}</span>{" "}
              owners
              {isFilterApplied && (
                <span className="ml-1 text-blue-600">(filtered by date)</span>
              )}
            </div>
            <div className="text-sm text-gray-600">
              {currentOwners.length > 0 &&
                `Page ${currentPage} of ${totalPages}`}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50 hover:bg-gray-50 border-b-2 border-gray-200">
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                  S/N
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                  <FontAwesomeIcon
                    icon={faUserTie}
                    className="mr-1.5 h-3 w-3"
                  />
                  Owner Name
                </TableHead>
                <TableHead className="hidden sm:table-cell text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                  <FontAwesomeIcon
                    icon={faEnvelope}
                    className="mr-1.5 h-3 w-3"
                  />
                  Email
                </TableHead>
                <TableHead className="hidden sm:table-cell text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                  <FontAwesomeIcon icon={faPhone} className="mr-1.5 h-3 w-3" />
                  Phone
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3 text-center">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2">
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="text-blue-600 h-8 w-8 animate-spin"
                      />
                      <div className="text-gray-600 text-sm">Loading...</div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : currentOwners.length > 0 ? (
                currentOwners.map((owner, idx) => (
                  <TableRow
                    key={owner.id || owner.shop_owner_id}
                    className="hover:bg-blue-50/30 transition-colors duration-150 border-b border-gray-100"
                  >
                    <TableCell className="py-2.5 px-3 text-sm text-gray-600">
                      {startIndex + idx + 1}
                    </TableCell>
                    <TableCell className="py-2.5 px-3 font-medium text-gray-900 text-sm">
                      {owner.owner_name}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell py-2.5 px-3 text-sm text-gray-600">
                      {owner.email}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell py-2.5 px-3 text-sm text-gray-600">
                      {owner.phone}
                    </TableCell>
                    <TableCell className="py-2.5 px-3">
                      <div className="flex justify-center">
                        <Button
                          size="sm"
                          variant="ghost"
                          title="View more details"
                          className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50"
                          onClick={() => {
                            setSelectedOwner(owner);
                            setIsViewModalOpen(true);
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faEye}
                            className="h-3.5 w-3.5"
                          />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2">
                      <FontAwesomeIcon
                        icon={faUsers}
                        className="text-gray-300 h-8 w-8"
                      />
                      <div className="text-gray-600 text-sm">
                        No owners found
                      </div>
                      <p className="text-gray-600 text-xs">
                        {searchTerm
                          ? "Try adjusting your search terms"
                          : canManageOwners
                            ? "Add your first owner to get started"
                            : "No owners yet"}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {totalItems > 0 && !loading && (
          <div className="px-4 py-2.5 border-t border-gray-100">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
            />
          </div>
        )}
      </Card>

      {/* Advanced Filter Modal */}
      {isAdvFilterOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-100">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
              <h3 className="text-lg font-bold text-gray-900">
                Advanced Filter
              </h3>
              <button
                onClick={() => setIsAdvFilterOpen(false)}
                className="w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center text-white transition-colors shadow-sm"
              >
                <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
              </button>
            </div>

            <div className="p-6 flex flex-col gap-5">
              <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                  <FontAwesomeIcon
                    icon={faFilter}
                    className="text-blue-600 h-4 w-4"
                  />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">
                    Filter Owners
                  </h4>
                  <p className="text-xs text-gray-600">
                    Select criteria to narrow down results
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <FontAwesomeIcon
                    icon={faCalendarAlt}
                    className="text-blue-500 h-3.5 w-3.5"
                  />
                  Filter by Date Added
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">
                      From
                    </label>
                    <input
                      type="date"
                      value={tempFilters.startDate}
                      onChange={(e) =>
                        setTempFilters({
                          ...tempFilters,
                          startDate: e.target.value,
                        })
                      }
                      className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50/50"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">
                      To
                    </label>
                    <input
                      type="date"
                      value={tempFilters.endDate}
                      onChange={(e) =>
                        setTempFilters({
                          ...tempFilters,
                          endDate: e.target.value,
                        })
                      }
                      className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50/50"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
              <Button
                variant="outline"
                onClick={clearAdvFilters}
                className="border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none px-6"
              >
                Clear All
              </Button>
              <Button
                onClick={applyAdvFilters}
                className="bg-blue-800 hover:bg-blue-900 text-white px-6 shadow-sm"
              >
                Apply Filters
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OwnerTab;
